import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  applyTransform,
  getDataset,
  listVersions,
  previewTransform,
  type Dataset,
  type DatasetVersion,
  type TransformPreview,
  type TransformStep,
} from '../api/datasets';
import { StatTile } from '../components/charts';
import { AppShell } from '../components/layout/AppShell';
import { JoinStepForm } from '../components/prepare/JoinStepForm';
import { StepForm } from '../components/prepare/StepForm';
import { stepDefinition } from '../components/prepare/stepCatalog';
import {
  Alert,
  Button,
  Card,
  DataTable,
  ErrorState,
  LoadingState,
  type Column,
} from '../components/ui';
import './PreparePage.css';

type State = 'loading' | 'ready' | 'error';

/**
 * `/datasets/:id/prepare` — build a recipe, see what it does, then apply it.
 *
 * Applying never changes the data you started from: it creates a new version,
 * so an analysis built on the old one keeps working.
 */
export function PreparePage() {
  const { datasetId = '' } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<State>('loading');
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [version, setVersion] = useState<DatasetVersion | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const [steps, setSteps] = useState<TransformStep[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [preview, setPreview] = useState<TransformPreview | null>(null);
  const [recipeError, setRecipeError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [loadedDataset, versions] = await Promise.all([
          getDataset(datasetId),
          listVersions(datasetId),
        ]);
        setDataset(loadedDataset);
        setVersion(versions.find((entry) => entry.status === 'READY') ?? null);
        setState('ready');
      } catch (caught) {
        setError(caught instanceof ApiError ? caught : null);
        setState('error');
      }
    }
    void load();
  }, [datasetId]);

  const runPreview = useCallback(
    async (recipe: TransformStep[]) => {
      if (!version || recipe.length === 0) {
        setPreview(null);
        setRecipeError(null);
        return;
      }

      setIsRunning(true);
      setRecipeError(null);
      try {
        setPreview(await previewTransform(datasetId, version.id, recipe));
      } catch (caught) {
        setPreview(null);
        setRecipeError(
          caught instanceof ApiError ? caught.message : 'That recipe could not be run.',
        );
      } finally {
        setIsRunning(false);
      }
    },
    [datasetId, version],
  );

  // The preview is the feedback loop: every change to the recipe re-runs it.
  useEffect(() => {
    void runPreview(steps);
  }, [steps, runPreview]);

  async function handleApply() {
    if (!version) return;

    setIsApplying(true);
    try {
      await applyTransform(datasetId, version.id, steps);
      void navigate(`/datasets/${datasetId}`);
    } catch (caught) {
      setRecipeError(
        caught instanceof ApiError ? caught.message : 'That recipe could not be applied.',
      );
    } finally {
      setIsApplying(false);
    }
  }

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Loading your data…" />
      </AppShell>
    );
  }

  if (state === 'error' || !dataset) {
    return (
      <AppShell>
        <ErrorState
          title="This dataset is not available"
          description={error?.message ?? 'It may have been deleted, or it is not yours.'}
        />
      </AppShell>
    );
  }

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Prepare {dataset.name}</h1>
        <p>
          Shape the data before you analyse it ·{' '}
          <Link to={`/datasets/${datasetId}`}>back to the dataset</Link>
        </p>
      </div>
      <Button
        onClick={() => void handleApply()}
        isLoading={isApplying}
        loadingLabel="Applying recipe"
        disabled={steps.length === 0 || Boolean(recipeError)}
      >
        Apply as a new version
      </Button>
    </div>
  );

  if (!version) {
    return (
      <AppShell header={header}>
        <Alert tone="warning" title="Nothing to prepare yet">
          This dataset has no version that finished processing.
        </Alert>
      </AppShell>
    );
  }

  const columns = preview?.columns ?? version.column_schema;
  const tableColumns: Column<Record<string, unknown>>[] = (preview?.columns ?? []).map(
    (column) => ({
      key: column.name,
      header: column.source_name,
      numeric: column.type === 'integer' || column.type === 'float',
      render: (row) => {
        const value = row[column.name];
        if (value === null || value === undefined) {
          return <span className="sc-table__null">empty</span>;
        }
        if (column.type === 'date' && typeof value === 'string') {
          return value.slice(0, 10);
        }
        return String(value);
      },
    }),
  );

  return (
    <AppShell header={header}>
      <Alert tone="info" title="Your original data stays as it is">
        Applying a recipe creates a new version of this dataset. Version {version.version} is never
        changed, so anything already built on it keeps working.
      </Alert>

      <Card
        title="Recipe"
        description={`Steps run in order, starting from version ${version.version}.`}
      >
        {steps.length === 0 && !isAdding && (
          <p className="sc-recipe__empty">No steps yet. Add one to start shaping the data.</p>
        )}

        {steps.length > 0 && (
          <ol className="sc-recipe">
            {steps.map((step, index) => (
              <li key={`${step.op}-${index}`} className="sc-recipe__step">
                <span className="sc-recipe__number sc-numeric">{index + 1}</span>
                <span className="sc-recipe__summary">
                  {stepDefinition(step.op)?.summarise(step) ?? step.op}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSteps(steps.filter((_, position) => position !== index))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ol>
        )}

        {isAdding && (
          <StepForm
            columns={columns}
            onAdd={(step) => {
              setSteps([...steps, step]);
              setIsAdding(false);
            }}
            onCancel={() => setIsAdding(false)}
          />
        )}

        {isJoining && (
          <JoinStepForm
            columns={columns}
            currentDatasetId={datasetId}
            onAdd={(step) => {
              setSteps([...steps, step]);
              setIsJoining(false);
            }}
            onCancel={() => setIsJoining(false)}
          />
        )}

        {!isAdding && !isJoining && (
          <div className="sc-recipe__actions">
            <Button variant="secondary" onClick={() => setIsAdding(true)}>
              Add a step
            </Button>
            <Button variant="secondary" onClick={() => setIsJoining(true)}>
              Join another dataset
            </Button>
          </div>
        )}
      </Card>

      {recipeError && (
        <Alert tone="danger" title="That step did not work">
          {recipeError}
        </Alert>
      )}

      {preview?.joins.map((analysis) => (
        <Card
          key={`${analysis.with_dataset_name}-${analysis.result_rows}`}
          title={`Join with ${analysis.with_dataset_name}`}
          description="What this match actually does to your rows."
        >
          <div className="sc-join__stats">
            <StatTile
              label="Rows matched"
              value={`${analysis.match_percentage}%`}
              detail={
                analysis.unmatched_left_rows
                  ? `${analysis.unmatched_left_rows.toLocaleString()} without a match`
                  : 'every row found a match'
              }
              tone={analysis.match_percentage < 100 ? 'warning' : 'default'}
            />
            <StatTile
              label="Rows after the join"
              value={analysis.result_rows.toLocaleString()}
              detail={`from ${analysis.left_rows.toLocaleString()}`}
              tone={analysis.row_multiplier > 1 ? 'warning' : 'default'}
            />
            <StatTile
              label="Key repeats"
              value={analysis.right_key_is_unique ? 'No' : 'Yes'}
              detail={
                analysis.right_key_is_unique
                  ? 'one row per match'
                  : `rows multiplied ${analysis.row_multiplier}×`
              }
              tone={analysis.right_key_is_unique ? 'default' : 'warning'}
            />
          </div>
        </Card>
      ))}

      {preview?.warnings.map((warning) => (
        <Alert key={warning} tone="warning" title="Worth knowing">
          {warning}
        </Alert>
      ))}

      {/* No preview card while the recipe is broken: the error above already
          says what happened, and an empty card would just sit there. */}
      {steps.length > 0 && !recipeError && (preview || isRunning) && (
        <Card
          title="Preview"
          description={
            preview
              ? `${preview.returned} of ${preview.row_count.toLocaleString()} rows${
                  preview.is_estimate
                    ? ` — estimated from the first ${preview.sampled_from.toLocaleString()} rows`
                    : ''
                }.`
              : 'Running your recipe…'
          }
        >
          {isRunning && !preview && <LoadingState label="Running your recipe…" />}
          {preview && (
            <DataTable
              caption={`Preview of ${dataset.name} after the recipe`}
              columns={tableColumns}
              rows={preview.rows}
              rowKey={(_row, index) => String(index)}
            />
          )}
        </Card>
      )}
    </AppShell>
  );
}
